import { z } from 'zod';

export const sopFormSchema = z.object({
  org_chart_node_id: z.preprocess(val => {
    // Convert to number, return undefined if empty string
    if (val === '' || val === null || val === undefined) return undefined;
    return Number(val);
  }, z.number({ required_error: 'Peran wajib diisi' }).min(1, 'Peran wajib diisi')),
  name: z
    .string()
    .min(1, 'Nama SOP wajib diisi')
    .min(3, 'Nama SOP minimal harus terdiri dari 3 karakter')
    .max(150, 'Nama SOP tidak boleh lebih dari 150 karakter')
    .refine(val => val.trim().length > 0, 'Nama SOP tidak boleh hanya berisi spasi'),
  job_description_id: z.preprocess(val => {
    // Convert to number, return undefined if empty string
    if (val === '' || val === null || val === undefined) return undefined;
    return Number(val);
  }, z.number({ required_error: 'Deskripsi pekerjaan harus dipilih' }).min(1, 'Deskripsi pekerjaan harus dipilih')),
  is_published: z.boolean().default(false),
});

export type SOPFormData = z.infer<typeof sopFormSchema> & { job_description_id?: number };

/**
 * SOP Metode SOP table row. Mirrors the BE `sop[]` shape. Man dan Method
 * wajib; Machine dan Material opsional, karena tidak setiap langkah SOP
 * melibatkan mesin atau material.
 */
export interface SopMethodValidationRow {
  man: string;
  machine: string;
  material: string;
  method: string;
}

/** Max length of each Metode SOP column, mirroring the BE `varchar(255)` limit. */
export const SOP_METHOD_MAX_LENGTH = 255;

/**
 * Validates the Metode SOP table that lives outside the react-hook-form
 * tree (kept in component state). Mirrors the BE rule
 * `validate:"required,min=1,dive"` so the user gets a specific Indonesian
 * error message before the request leaves the browser.
 *
 * Returns null when the table is acceptable, otherwise a one-line
 * error message ready for a toast.
 */
export const validateSopMethodTable = (items: SopMethodValidationRow[]): string | null => {
  if (!items.length) return 'Minimal 1 baris Metode SOP wajib diisi';
  const hasMissing = items.some(it => !it.man.trim() || !it.method.trim());
  if (hasMissing) {
    return 'Kolom Man dan Method wajib diisi pada setiap baris Metode SOP';
  }
  const hasOverLength = items.some(
    it =>
      it.man.length > SOP_METHOD_MAX_LENGTH ||
      it.machine.length > SOP_METHOD_MAX_LENGTH ||
      it.material.length > SOP_METHOD_MAX_LENGTH ||
      it.method.length > SOP_METHOD_MAX_LENGTH,
  );
  if (hasOverLength) {
    return `Setiap kolom Metode SOP maksimal ${SOP_METHOD_MAX_LENGTH} karakter`;
  }
  return null;
};

/**
 * Validates a single Metode SOP row before it is appended to the table.
 * Returns null when valid, or the list of missing field labels otherwise.
 */
export const findMissingSopMethodFields = (row: SopMethodValidationRow): string[] => {
  const missing: string[] = [];
  if (!row.man.trim()) missing.push('Man');
  if (!row.method.trim()) missing.push('Method');
  return missing;
};
